import { AutomatedParameter } from '@e2m-customer-portal/domains';
import { InfoTooltip, NumberInput, NumberInputProps } from '@e2m-customer-portal/ui';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface AutomatedParameterInputProps extends Omit<NumberInputProps, 'value' | 'onChange'> {
  parameter: AutomatedParameter<number>;
  liveParameterLabel?: string;
  valueFactor?: number;
  onParameterUpdate: (parameter: AutomatedParameter<number>) => void;
}

export function AutomatedParameterInput({
  parameter,
  liveParameterLabel,
  valueFactor = 1,
  onParameterUpdate,
  ...props
}: Readonly<AutomatedParameterInputProps>): ReactElement<AutomatedParameterInputProps> {
  const [t] = useTranslation();

  const onCheckboxChange = () => {
    onParameterUpdate({
      ...parameter,
      enable_live_parameter: !parameter.enable_live_parameter,
    });
  };

  const onNumberChange = (value: number) => {
    onParameterUpdate({
      ...parameter,
      default_value: value != null && !Number.isNaN(value) ? Math.round(value) / valueFactor : null,
    });
  };

  return (
    <>
      <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
        <input
          type="checkbox"
          className="w-4 h-4 mr-2 accent-green-200"
          checked={parameter.enable_live_parameter}
          disabled={props.disabled}
          onChange={onCheckboxChange}
        />
        {liveParameterLabel || t('COPT_AUTOMATED_PARAMETER')}
        <InfoTooltip text={t('COPT_AUTOMATED_PARAMETER_TOOLTIP')} />
      </label>
      {/* @ts-expect-error Typescript expects the overloaded `NumberInput` to support `NullableMeasure`, which is neither needed nor wanted here. */}
      <NumberInput
        value={
          parameter.default_value === null ? parameter.default_value : Math.round(parameter.default_value * valueFactor)
        }
        onChange={onNumberChange}
        {...props}
      />
    </>
  );
}
